import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MapPin, Plus, X, Save, Calendar, ChevronRight, Pencil, Trash2, Lock } from "lucide-react";
import ConfirmDialog from "../components/ConfirmDialog";

const FULL_ROLES = new Set(["super_admin", "admin", "manager"]);

const STATUS_BADGE = {
  open: { label: "Open", bg: "rgba(74,124,89,0.15)", color: "#2D5E3A" },
  closed: { label: "Closed", bg: "rgba(140,158,138,0.18)", color: "#5B6B5B" },
};

const fmtDate = (iso) => {
  try { return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
};
const fmtMoney = (n, c = "EUR") => `${c === "EUR" ? "€" : ""}${Number(n || 0).toFixed(2)}`;
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function Trips() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const role = user?.role;
  const canCreate = FULL_ROLES.has(role);

  const filter = params.get("status") || "all";
  const [trips, setTrips] = useState(null);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", location: "", start_date: todayIso(), end_date: todayIso(), notes: "" });
  const [busy, setBusy] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get(`/trips${filter !== "all" ? `?status=${filter}` : ""}`);
      setTrips(data);
    } catch (e) {
      setError(formatApiError(e));
      setTrips([]);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const openCount = useMemo(() => (trips || []).filter((t) => t.status === "open").length, [trips]);

  const submitCreate = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/trips", {
        name: form.name.trim(),
        location: form.location.trim() || null,
        start_date: form.start_date,
        end_date: form.end_date,
        notes: form.notes.trim() || null,
      });
      setCreateOpen(false);
      navigate(`/trips/${data.id}`);
    } catch (err) {
      setError(formatApiError(err));
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/trips/${delTarget.id}`);
      setDelTarget(null);
      await load();
    } catch (err) { setError(formatApiError(err)); setDelTarget(null); }
  };

  const doClose = async (trip) => {
    try {
      await api.post(`/trips/${trip.id}/close`);
      await load();
    } catch (err) { setError(formatApiError(err)); }
  };

  const setFilter = (next) => {
    if (next === "all") setParams({});
    else setParams({ status: next });
  };

  return (
    <div data-testid="trips-page">
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <div className="se-label">Travel</div>
          <h1 className="font-heading mt-1" style={{ fontSize: 32 }} data-testid="trips-heading">Trips</h1>
          <p className="text-sm mt-1" style={{ color: "#8C9E8A" }}>
            Track competitions, transports and on-the-go expenses across horses and staff.
          </p>
        </div>
        {canCreate && (
          <button className="se-btn" onClick={() => { setForm({ name: "", location: "", start_date: todayIso(), end_date: todayIso(), notes: "" }); setCreateOpen(true); }} data-testid="trips-new-button">
            <Plus size={14} /> New trip
          </button>
        )}
      </div>

      <div
        className="flex items-center gap-2 mb-5 sticky z-10 -mx-5 md:-mx-10 px-5 md:px-10 py-3 backdrop-blur"
        style={{
          top: 0,
          background: "rgba(245, 239, 230, 0.85)",
          borderBottom: "0.5px solid rgba(212, 169, 106, 0.2)",
        }}
        data-testid="trips-filter-bar"
      >
        {["all", "open", "closed"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`se-btn ${filter === s ? "" : "se-btn-ghost"}`}
            style={{ padding: "6px 14px", fontSize: 13 }}
            data-testid={`trips-filter-${s}`}
          >
            {s === "all" ? "All" : s === "open" ? "Open" : "Closed"}
            {s === "open" && trips && openCount > 0 ? <span className="ml-1" style={{ opacity: 0.7 }}>· {openCount}</span> : ""}
          </button>
        ))}
      </div>

      {error && <div className="mb-4 text-sm px-3 py-2 rounded-lg" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="trips-error">{error}</div>}

      <div className="space-y-3">
        {trips === null && <div className="se-card text-sm" style={{ color: "#8C9E8A" }}>Loading…</div>}
        {trips !== null && trips.length === 0 && (
          <div className="se-card text-sm" style={{ color: "#8C9E8A" }} data-testid="trips-empty">
            {filter === "all"
              ? "No trips yet. Create one to start logging on-the-road expenses."
              : `No ${filter} trips.`}
          </div>
        )}
        {(trips || []).map((t) => {
          const badge = STATUS_BADGE[t.status] || STATUS_BADGE.open;
          return (
            <div
              key={t.id}
              className="se-card flex items-start gap-3"
              data-testid={`trip-row-${t.id}`}
            >
              <button
                type="button"
                onClick={() => navigate(`/trips/${t.id}`)}
                className="flex items-start gap-3 flex-1 text-left"
                style={{ cursor: "pointer" }}
                data-testid={`trip-open-${t.id}`}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(124,92,58,0.15)", color: "#7C5C3A" }}>
                  <MapPin size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading" style={{ fontSize: 18 }}>{t.name}</span>
                    <span className="se-badge" style={{ background: badge.bg, color: badge.color }} data-testid={`trip-status-${t.id}`}>{badge.label}</span>
                  </div>
                  <div className="text-xs mt-1 flex items-center gap-3 flex-wrap" style={{ color: "#8C9E8A" }}>
                    {t.location && <span className="flex items-center gap-1"><MapPin size={11} /> {t.location}</span>}
                    <span className="flex items-center gap-1"><Calendar size={11} /> {fmtDate(t.start_date)} – {fmtDate(t.end_date)}</span>
                    <span>{t.horse_count} horse{t.horse_count === 1 ? "" : "s"}</span>
                    <span>{t.staff_count} staff</span>
                    <span style={{ color: "#3B2F1E", fontWeight: 600 }}>{fmtMoney(t.expense_total, t.currency)}</span>
                  </div>
                </div>
                <ChevronRight size={16} style={{ color: "#8C9E8A", marginTop: 12 }} />
              </button>
              {canCreate && (
                <div className="flex gap-1 shrink-0">
                  {t.status === "open" && (
                    <button className="se-btn se-btn-ghost" style={{ padding: 6 }} onClick={() => doClose(t)} title="Close trip" data-testid={`trip-close-${t.id}`}>
                      <Lock size={14} />
                    </button>
                  )}
                  <button className="se-btn se-btn-ghost" style={{ padding: 6 }} onClick={() => navigate(`/trips/${t.id}?edit=1`)} title="Edit" data-testid={`trip-edit-${t.id}`}>
                    <Pencil size={14} />
                  </button>
                  <button className="se-btn se-btn-ghost" style={{ padding: 6, color: "#C0392B" }} onClick={() => setDelTarget(t)} title="Delete" data-testid={`trip-delete-${t.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {createOpen && (
        <div className="fixed inset-0 z-30 flex items-center justify-center p-4" style={{ background: "rgba(59,47,30,0.5)" }} onClick={() => setCreateOpen(false)}>
          <form className="se-card w-full max-w-md" onClick={(e) => e.stopPropagation()} onSubmit={submitCreate} data-testid="trip-create-form">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading" style={{ fontSize: 20 }}>New trip</h3>
              <button type="button" className="se-btn se-btn-ghost" onClick={() => setCreateOpen(false)} style={{ padding: 6 }}><X size={14} /></button>
            </div>
            <label className="se-label block mb-1">Name *</label>
            <input className="se-input mb-3" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Athens Classic June 2026" data-testid="trip-create-name" />
            <label className="se-label block mb-1">Location</label>
            <input className="se-input mb-3" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Athens, GR" data-testid="trip-create-location" />
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="se-label block mb-1">Start date *</label>
                <input type="date" className="se-input" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="trip-create-start" />
              </div>
              <div>
                <label className="se-label block mb-1">End date *</label>
                <input type="date" className="se-input" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="trip-create-end" />
              </div>
            </div>
            <label className="se-label block mb-1">Notes</label>
            <textarea className="se-input" style={{ height: 80, padding: "10px 14px" }} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="trip-create-notes" />
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" className="se-btn se-btn-ghost" onClick={() => setCreateOpen(false)}>Cancel</button>
              <button type="submit" className="se-btn" disabled={busy} data-testid="trip-create-submit"><Save size={14} /> {busy ? "Saving…" : "Create trip"}</button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={!!delTarget}
        onClose={() => setDelTarget(null)}
        onConfirm={doDelete}
        title={`Delete trip '${delTarget?.name}'?`}
        description="This trip and all its expenses will be moved to trash."
        confirmLabel="Yes, delete"
        testid="trip-delete-confirm"
      />
    </div>
  );
}
